<template>
  <div class="default-modal login-modal">
    <div class="login-modal__header">
      <span class="login-modal__title" v-if="step === 1">Вход</span>
      <button
        type="button"
        class="default-modal__close"
        @click="$emit('close')"
        aria-label="Закрыть модальное окно"
      >
        <RemixIconComponent category="system" name="close-fill" />
      </button>
    </div>
    <div class="login-modal__body">
      <form @submit.prevent="submit" class="login-modal__form">
        <InputComponent
          v-if="step === 1"
          v-model="form.phone.value"
          :errors="form.phone.errors"
          placeholder="Номер телефона"
          mask="+7 (###) ###-##-##"
          type="tel"
          is-phone
        />
        <template v-if="step === 2">
          <!-- Заголовок подтверждения -->
          <div class="login-modal__verification-header">
            <h3 class="login-modal__verification-title">
              Подтверждение номера телефона
            </h3>
          </div>

          <!-- Информация о вызове/SMS -->
          <div class="login-modal__verification-info">
            <template v-if="verificationMethod === 'flashcall'">
              <p class="login-modal__phone-info">
                Мы звоним на {{ formatPhoneNumber(form.phone.value) }}
              </p>
              <p class="login-modal__instruction">
                Введите последние 4 цифры номера, с которого поступает звонок
              </p>
            </template>
            <template v-else-if="verificationMethod === 'sms'">
              <p class="login-modal__phone-info">
                SMS отправлена на {{ formatPhoneNumber(form.phone.value) }}
              </p>
              <p class="login-modal__instruction">
                Введите код из SMS-сообщения
              </p>
            </template>
            <template v-else>
              <p class="login-modal__phone-info">
                Подтверждаем {{ formatPhoneNumber(form.phone.value) }}
              </p>
              <p class="login-modal__instruction">Введите код подтверждения</p>
            </template>
          </div>

          <!-- Поле для ввода кода -->
          <InputComponent
            v-model="form.code.value"
            :errors="form.code.errors"
            placeholder="Введите код"
            type="text"
            maxlength="4"
          />
        </template>
        <GrecaptchaComponent
          v-show="step === 1"
          ref="recaptchaWidget"
          :errors="form.captcha.errors"
        />
        <!-- SMS Fallback секция (показывается только на втором шаге и если доступна) -->
        <template v-if="step === 2 && showSmsFallbackButton">
          <div class="login-modal__divider"></div>

          <div class="login-modal__sms-fallback">
            <p class="login-modal__sms-fallback-text">
              Не получается принять звонок?
            </p>
            <button
              type="button"
              @click="sendSmsFallback"
              :disabled="smsButtonDisabled || loading"
              class="btn btn--outline login-modal__sms-btn"
            >
              <template v-if="smsButtonDisabled">
                Отправить SMS-код ({{ countdown }})
              </template>
              <template v-else> Отправить SMS-код </template>
            </button>
          </div>
        </template>

        <!-- Обычная логика повторной отправки кода (для SMS режима) -->
        <button
          v-if="canSendCodeAgain && step === 2 && !showSmsFallbackButton"
          type="button"
          @click="sendPhoneVerification"
          class="btn btn--outline login-modal__sms-btn"
        >
          Отправить код повторно
        </button>
        <div
          v-else-if="step === 2 && !showSmsFallbackButton"
          class="login-modal__code-wrapper"
        >
          <span>Запросить новый код через</span>
          <span>00:{{ sendCodeTimer }}</span>
        </div>
        <button
          class="btn btn--main login-modal__btn"
          :disabled="loading"
          :aria-label="step === 2 ? 'Вход' : 'Получить код'"
        >
          <template v-if="loading"> Загрузка... </template>
          <template v-else-if="step === 2"> Вход </template>
          <template v-else> Получить код </template>
        </button>
      </form>
    </div>
    <div class="login-modal__footer">
      <span class="login-modal__policy">
        Нажимая на кнопку, вы соглашаетесь на
        <router-link
          :to="{
            name: 'static',
            query: { type: $store.state._types.ACT_PROCESSING_PERSONAL_DATA },
          }"
          target="_blank"
        >
          сбор и обработку персональных данных
        </router-link>
      </span>
    </div>
  </div>
</template>

<script>
import RemixIconComponent from "components/RemixIconComponent.vue";
import InputComponent from "components/inputs/InputComponent.vue";
import PHONE_CONFIRM from "@/graphql/mutations/UserPhoneConfirm.graphql";
import SEND_PHONE_VERIFICATION from "@/graphql/mutations/SendPhoneVerification.graphql";
import SEND_SMS_FALLBACK from "@/graphql/mutations/SendSmsFallback.graphql";
import { confirmUser, parseGqlErrors } from "@/utils";
import GrecaptchaComponent from "components/GrecaptchaComponent.vue";

export default {
  name: "LoginModal",
  components: {
    GrecaptchaComponent,
    InputComponent,
    RemixIconComponent,
  },
  data() {
    return {
      loading: false,
      sendCodeInterval: null,
      sendCodeTimer: null,
      canSendCodeAgain: false,
      step: 1,
      // Новые состояния для flashcall + SMS fallback
      smsButtonDisabled: false,
      countdown: 0,
      verificationMethod: null,
      backendMessage: null,
      showSmsFallbackButton: false,
      socials: [
        {
          title: "VK",
          link: this.$store.state._env.HOST_ENDPOINT + "/auth/vkontakte",
          icon: "vk",
        },
        {
          title: "Google",
          link: this.$store.state._env.HOST_ENDPOINT + "/auth/google",
          icon: "google",
        },
        {
          title: "Apple",
          link: this.$store.state._env.HOST_ENDPOINT + "/auth/apple",
          icon: "apple",
        },
      ],
      form: {
        phone: {
          value: null,
          errors: [],
        },
        code: {
          value: null,
          errors: [],
        },
        captcha: {
          value: null,
          errors: [],
        },
        captcha_key: {
          value: null,
          errors: [],
        },
      },
    };
  },
  // watch: {
  //   "form.captcha.errors"(val) {
  //     if (val && val.length) {
  //       this.showCaptchaModal();
  //     }
  //   },
  // },
  destroyed() {
    this.removeInterval();
  },
  methods: {
    confirmUser,
    parseGqlErrors,
    resetErrors() {
      Object.keys(this.form).forEach((key) => {
        this.form[key].errors = [];
      });
    },
    submit() {
      if (this.step === 1) {
        // this.showCaptchaModal();
        this.sendPhoneVerification();
      } else {
        this.confirmPhone();
      }
    },
    // showCaptchaModal() {
    //   this.$store.state._modals.push({
    //     component: CaptchaModal,
    //     options: {
    //       login: this.login,
    //       set: (field, value) => {
    //         this.form[field].value = value;
    //       },
    //     },
    //   });
    // },
    sendPhoneVerification() {
      if (!this.loading) {
        this.loading = true;
        this.resetErrors();
        this.backendMessage = null;
        this.$apollo.provider.clients.lk
          .mutate({
            mutation: SEND_PHONE_VERIFICATION,
            variables: {
              phone: this.form.phone.value
                ? this.form.phone.value.replace(/[\s-()]/g, "")
                : null,
              captcha: this.$refs.recaptchaWidget
                ? this.$refs.recaptchaWidget.response()
                : null,
              // captcha_key: this.form.captcha_key.value,
            },
          })
          .then(({ data }) => {
            this.loading = false;
            const result = data.SendPhoneVerification;
            this.backendMessage = result.message;
            this.verificationMethod = result.method;

            if (result.code === 200 || result.code === 202) {
              // Успешный флешколл или флешколл не удался
              this.step = 2;
              this.showSmsFallbackButton = true;
              this.startSmsButtonTimer();
            } else {
              // Обработка других кодов ошибок
              this.handleErrorResponse(result);
            }
          })
          .catch(({ graphQLErrors, networkError }) => {
            this.loading = false;
            if (networkError) {
              this.backendMessage = "Ошибка сети, попробуйте позже";
              console.error("Network error:", networkError);
            } else {
              this.parseGqlErrors(graphQLErrors);
            }
          });
      }
    },
    sendSmsFallback() {
      if (!this.loading && !this.smsButtonDisabled) {
        this.loading = true;
        this.resetErrors();
        this.$apollo.provider.clients.lk
          .mutate({
            mutation: SEND_SMS_FALLBACK,
            variables: {
              phone: this.form.phone.value
                ? this.form.phone.value.replace(/[\s-()]/g, "")
                : null,
            },
          })
          .then(({ data }) => {
            this.loading = false;
            const result = data.SendSmsFallback;
            this.backendMessage = result.message;
            this.verificationMethod = result.method;

            if (result.code === 200) {
              // SMS успешно отправлена
              this.showSmsFallbackButton = false;
              this.removeInterval();
              this.startInterval(); // Запускаем обычный таймер для повторной отправки
            } else {
              this.handleErrorResponse(result);
            }
          })
          .catch(({ graphQLErrors, networkError }) => {
            this.loading = false;
            if (networkError) {
              this.backendMessage = "Ошибка сети, попробуйте позже";
              console.error("Network error:", networkError);
            } else {
              this.parseGqlErrors(graphQLErrors);
            }
          });
      }
    },
    handleErrorResponse(result) {
      this.backendMessage = result.message;
      if (result.code >= 400) {
        console.error("API error:", result);
      }
    },
    login() {
      // Старый метод логина (оставляем для совместимости)
      this.sendPhoneVerification();
    },
    confirmPhone() {
      if (!this.loading) {
        this.loading = true;
        this.resetErrors();
        this.$apollo.provider.clients.lk
          .mutate({
            mutation: PHONE_CONFIRM,
            variables: {
              code: this.form.code.value,
            },
          })
          .then(({ data }) => {
            if (data && data.UserPhoneConfirm) {
              this.removeInterval();
              this.confirmUser({
                store: this.$store,
                router: this.$router,
                apollo: this.$apollo.provider.clients,
                data: data.UserPhoneConfirm,
              });
            }
            this.$store.state._modals = [];
            this.loading = false;
          })
          .catch(({ graphQLErrors }) => {
            this.loading = false;
            this.parseGqlErrors(graphQLErrors);
          });
      }
    },
    removeInterval() {
      clearInterval(this.sendCodeInterval);
    },
    startInterval() {
      this.removeInterval();
      this.sendCodeTimer = 59;
      this.canSendCodeAgain = false;
      this.sendCodeInterval = setInterval(() => {
        if (this.sendCodeTimer <= 1) {
          this.removeInterval();
          this.canSendCodeAgain = true;
        }
        this.sendCodeTimer = parseInt(this.sendCodeTimer) - 1;
        if (this.sendCodeTimer < 10) {
          this.sendCodeTimer = "0" + this.sendCodeTimer;
        }
      }, 1000);
    },
    startSmsButtonTimer() {
      this.smsButtonDisabled = true;
      this.countdown = 60;
      const smsTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(smsTimer);
          this.smsButtonDisabled = false;
          this.countdown = 0;
        }
      }, 1000);
    },
    formatPhoneNumber(phone) {
      if (!phone) return "";

      const digits = phone.replace(/\D/g, "");

      // Нормализуем номер до 10 цифр (убираем ведущую 7 или 8)
      const normalizedDigits =
        digits.startsWith("7") && digits.length === 11
          ? digits.slice(1)
          : digits.startsWith("8") && digits.length === 11
          ? digits.slice(1)
          : digits.slice(-10); // берем последние 10 цифр

      // Проверяем валидность длины
      if (normalizedDigits.length !== 10) {
        return phone; // возвращаем как есть если не валиден
      }

      // Форматируем в +7 (XXX) XXX-XX-XX
      return `+7 (${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(
        3,
        6
      )}-${normalizedDigits.slice(6, 8)}-${normalizedDigits.slice(8)}`;
    },
  },
};
</script>

<style lang="stylus">
.login-modal{
  width 100%
  max-width 424px
  border-radius 16px
  padding: 33px 32px

  &__header{
    display flex
    justify-content space-between
    align-items center
  }

  &__title{
    font-weight: 500;
    font-size: 24px;
    line-height: 120%;
    color: var(--gray-900)
  }

  &__body {
    width 100%
    padding-top: 17px;
  }

  &__form{
    display flex
    flex-direction column
    gap 16px
  }

  &__btn{
    width 100%
    height 46px
    border-radius: 16px !important
  }

  &__footer {
    display flex
    flex-direction column
    padding-top: 10px;
    gap: 32px
  }

  &__policy {
    font-size: 0.75em
    line-height: 120%;
    text-align: center;
    color: var(--body_muted)

    a {
      color: var(--body_muted)
      text-decoration underline

      &:hover {
        color var(--main)
      }
    }
  }

  &__code-wrapper {
    font-size: 0.875rem
    color var(--gray-700)
    text-align center
    line-height 6px
    display flex
    gap 4px
    justify-content center
    //text-decoration underline
    //cursor: pointer;
  }

  &__code-again {
    color var(--main)
    text-align center
    font-size: 0.875rem
    text-decoration underline
    cursor: pointer;
    line-height 6px
  }

  &__backend-message {
    font-size: 0.875rem
    line-height: 1.2
    text-align: center
    color: var(--gray-700)
    padding: 8px 12px
    background: var(--gray-50)
    border-radius: 8px
    margin-bottom: 8px
  }

  &__verification-header {
    text-align: center
    margin-bottom: 24px
  }

  &__verification-title {
    font-size: 1.125rem
    font-weight: 600
    color: var(--gray-900)
    margin: 0
  }

  &__verification-info {
    text-align: center
    margin-bottom: 32px
  }

  &__phone-info {
    font-size: 1rem
    font-weight: 500
    color: var(--gray-900)
    margin: 0 0 12px 0
  }

  &__instruction {
    font-size: 0.875rem
    color: var(--gray-600)
    margin: 0
    line-height: 1.4
  }

  &__timing-info {
    text-align: center
    font-size: 0.875rem
    color: var(--gray-500)
    margin-top: 16px
    margin-bottom: 24px
  }

  &__divider {
    height: 1px
    background: var(--border-color-2)
    margin: 32px 0 24px 0
    position: relative

    &::before {
      content: ""
      position: absolute
      left: 0
      right: 0
      height: 1px
      background: repeating-linear-gradient(
        to right,
        var(--border-color-2) 0px,
        var(--border-color-2) 4px,
        transparent 4px,
        transparent 8px
      )
    }
  }

  &__sms-fallback {
    text-align: center
  }

  &__sms-fallback-text {
    font-size: 0.875rem
    color: var(--gray-700)
    margin: 0 0 16px 0
  }

  &__sms-btn {
    width: 100%
    height: 44px
    border-radius: 12px !important
    font-size: 0.875rem
    font-weight: 500

    &:disabled {
      opacity: 0.6
      cursor: not-allowed
    }
  }

  &__or-line {
    display grid
    grid-template-columns 1fr auto 1fr
    align-items center
    gap: 16px
    font-size: .875em
    line-height: 19px;

    &:before,
    &:after {
      content ""
      display block
      height 1px
      background var(--border-color-2)
    }
  }

  &__socials {
    display flex
    align-items center
    justify-content center
    gap: 32px

    &-item {
      display flex
      width 48px
      height: 48px

      .icon {
        width 100%
        height 100%
      }
    }
  }
}
</style>
